import {
  Badge,
  Card,
  mergeClasses,
  Text,
  Tooltip,
} from '@fluentui/react-components';
import {
  ChevronDown16Regular,
  ChevronRight16Regular,
} from '@fluentui/react-icons';
import {
  createContext,
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { CSSProperties } from 'react';
import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';

import type {
  TeamsAgentDebugTrajectoryEntry,
  TeamsAgentDebugTrajectoryMessage,
} from '../../../types/debug.js';

import type { DebugTrajectoryTimelineProps } from './DebugTrajectoryTimeline.interface.js';
import {
  FLAME_BAR_END_VAR,
  FLAME_BAR_LEFT_VAR,
  useStyles,
} from './DebugTrajectoryTimeline.style.js';

function formatTimestamp(timestamp: string): string {
  if (!timestamp) return 'Time unavailable';
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? timestamp : date.toLocaleString();
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function stringifyValue(value: unknown): string {
  if (typeof value === 'string') return value;
  return JSON.stringify(value, null, 2);
}

/**
 * Join point between a tool call and the tool result that answers it. Tool
 * results arrive as their own message in an execution trajectory, so the only
 * link back to the originating call is the tool use id.
 */
interface ToolCallLink {
  name: string;
  callIndex?: number;
  resultIndex?: number;
}

type ToolLinkIndex = ReadonlyMap<string, ToolCallLink>;

interface ToolLinkContextValue {
  index: ToolLinkIndex;
  onNavigate: (entryIndex: number) => void;
}

const EMPTY_TOOL_LINK_INDEX: ToolLinkIndex = new Map<string, ToolCallLink>();

const ToolLinkContext = createContext<ToolLinkContextValue>({
  index: EMPTY_TOOL_LINK_INDEX,
  onNavigate: () => undefined,
});

function ToolLinkButton({
  label,
  targetIndex,
}: {
  label: string;
  targetIndex: number;
}) {
  const styles = useStyles();
  const { onNavigate } = useContext(ToolLinkContext);

  return (
    <button
      className={styles.toolLinkButton}
      onClick={(event) => {
        event.stopPropagation();
        onNavigate(targetIndex);
      }}
      type="button"
    >
      {label}
    </button>
  );
}

function MessageContent({
  collapsed = false,
  entryIndex,
  message,
}: {
  collapsed?: boolean;
  entryIndex: number;
  message: TeamsAgentDebugTrajectoryMessage;
}) {
  const styles = useStyles();
  const { index: toolLinkIndex } = useContext(ToolLinkContext);
  const blocks = Array.isArray(message.content)
    ? message.content
    : [message.content];
  const toolUseBlocks = blocks.filter(
    (block): block is Record<string, unknown> =>
      isRecord(block) && block.type === 'tool_use',
  );
  // Collapsed cards keep every block's header and clamp only its body, so the
  // shape of the message (thinking, parallel calls, results) stays visible.
  const textClassName = collapsed ? styles.clampedBody : undefined;
  const codeClassName = mergeClasses(
    styles.code,
    collapsed && styles.clampedBody,
  );

  return (
    <div className={styles.content}>
      {blocks.map((block, index) => {
        if (!isRecord(block)) {
          return (
            <div className={styles.contentBlock} key={index.toString()}>
              <div className={textClassName}>{stringifyValue(block)}</div>
            </div>
          );
        }

        if (block.type === 'text' && typeof block.text === 'string') {
          if (message.role === 'assistant') {
            return (
              <div
                className={mergeClasses(styles.contentBlock, styles.toolBlock)}
                key={index.toString()}
              >
                <div className={styles.toolHeader}>
                  <Badge appearance="tint" color="brand">
                    Response
                  </Badge>
                </div>
                <div className={textClassName}>{block.text}</div>
              </div>
            );
          }
          return (
            <div className={styles.contentBlock} key={index.toString()}>
              <div className={textClassName}>{block.text}</div>
            </div>
          );
        }

        if (block.type === 'thinking' && typeof block.thinking === 'string') {
          return (
            <div
              className={mergeClasses(styles.contentBlock, styles.toolBlock)}
              key={index.toString()}
            >
              <div className={styles.toolHeader}>
                <Badge appearance="tint" color="informative">
                  Thinking
                </Badge>
              </div>
              <div className={textClassName}>{block.thinking}</div>
            </div>
          );
        }

        if (block.type === 'tool_use') {
          const toolName =
            typeof block.name === 'string' ? block.name : 'unknown';
          const callId = typeof block.id === 'string' ? block.id : '';
          const resultIndex = callId
            ? toolLinkIndex.get(callId)?.resultIndex
            : undefined;
          const position = toolUseBlocks.indexOf(block) + 1;

          return (
            <div
              className={mergeClasses(styles.contentBlock, styles.toolBlock)}
              key={index.toString()}
            >
              <div className={styles.toolHeader}>
                <Badge appearance="tint" color="warning">
                  {toolUseBlocks.length > 1
                    ? `Tool call ${position.toString()} of ${toolUseBlocks.length.toString()}`
                    : 'Tool call'}
                </Badge>
                <span className={styles.toolName}>{toolName}</span>
                {callId && <span className={styles.toolId}>{callId}</span>}
                {resultIndex !== undefined && resultIndex !== entryIndex && (
                  <ToolLinkButton
                    label="View tool result"
                    targetIndex={resultIndex}
                  />
                )}
              </div>
              <pre className={codeClassName}>
                {stringifyValue(block.input ?? {})}
              </pre>
            </div>
          );
        }

        if (block.type === 'tool_result') {
          const callId =
            typeof block.tool_use_id === 'string' ? block.tool_use_id : '';
          const link = callId ? toolLinkIndex.get(callId) : undefined;
          const isError = block.is_error === true;

          return (
            <div
              className={mergeClasses(styles.contentBlock, styles.toolBlock)}
              key={index.toString()}
            >
              <div className={styles.toolHeader}>
                <Badge appearance="tint" color={isError ? 'danger' : 'success'}>
                  {isError ? 'Tool result (error)' : 'Tool result'}
                </Badge>
                {link?.name && (
                  <span className={styles.toolName}>{link.name}</span>
                )}
                {callId && <span className={styles.toolId}>{callId}</span>}
                {link?.callIndex !== undefined &&
                  link.callIndex !== entryIndex && (
                    <ToolLinkButton
                      label="View tool call"
                      targetIndex={link.callIndex}
                    />
                  )}
              </div>
              <pre className={codeClassName}>
                {stringifyValue(block.content ?? '')}
              </pre>
            </div>
          );
        }

        return (
          <div className={styles.contentBlock} key={index.toString()}>
            <pre className={codeClassName}>{stringifyValue(block)}</pre>
          </div>
        );
      })}
    </div>
  );
}

function EventContent({
  collapsed,
  entry,
  entryIndex,
}: {
  collapsed?: boolean;
  entry: TeamsAgentDebugTrajectoryEntry;
  entryIndex: number;
}) {
  if (entry.message) {
    return (
      <MessageContent
        collapsed={collapsed}
        entryIndex={entryIndex}
        message={entry.message}
      />
    );
  }
  if (entry.type === 'tool_result') {
    return (
      <MessageContent
        collapsed={collapsed}
        entryIndex={entryIndex}
        message={{
          role: 'tool',
          content: [
            {
              type: 'tool_result',
              tool_use_id: entry.toolUseId,
              is_error: entry.isError,
              content: entry.content,
            },
          ],
        }}
      />
    );
  }
  return null;
}

function getEventLabel(entry: TeamsAgentDebugTrajectoryEntry): string {
  const role = entry.message?.role;
  return role && role.length > 0 ? role : entry.type;
}

type FlameEventKind =
  | 'system'
  | 'user'
  | 'assistant'
  | 'tool-use'
  | 'tool-result'
  | 'other';

interface FlameEvent {
  entry: TeamsAgentDebugTrajectoryEntry;
  index: number;
  key: string;
  timestampMs: number;
  offsetMs: number;
  durationMs: number;
  label: string;
  kind: FlameEventKind;
  isError: boolean;
  /** Events folded into this bar, such as the result a tool call ends on. */
  linkedIndexes?: readonly number[];
  /**
   * Events selected alongside this bar but outside its span, such as the
   * results of the calls a tool-calling turn issued.
   */
  relatedIndexes?: readonly number[];
}

function getSelectionGroup(event: FlameEvent): readonly number[] {
  return [...(event.linkedIndexes ?? []), ...(event.relatedIndexes ?? [])];
}

function getMessageBlocks(entry: TeamsAgentDebugTrajectoryEntry): unknown[] {
  if (entry.message) {
    return Array.isArray(entry.message.content)
      ? entry.message.content
      : [entry.message.content];
  }
  if (entry.type === 'tool_result') {
    return [
      {
        type: 'tool_result',
        tool_use_id: entry.toolUseId,
        is_error: entry.isError,
        content: entry.content,
      },
    ];
  }
  return [];
}

function findContentBlock(
  entry: TeamsAgentDebugTrajectoryEntry,
  type: string,
): Record<string, unknown> | undefined {
  return getMessageBlocks(entry).find(
    (block): block is Record<string, unknown> =>
      isRecord(block) && block.type === type,
  );
}

function findContentBlocks(
  entry: TeamsAgentDebugTrajectoryEntry,
  type: string,
): Record<string, unknown>[] {
  return getMessageBlocks(entry).filter(
    (block): block is Record<string, unknown> =>
      isRecord(block) && block.type === type,
  );
}

function getToolUseName(block: Record<string, unknown>): string {
  return typeof block.name === 'string' && block.name.length > 0
    ? block.name
    : 'unknown';
}

function buildToolLinkIndex(
  entries: TeamsAgentDebugTrajectoryEntry[],
): ToolLinkIndex {
  const index = new Map<string, ToolCallLink>();

  const upsert = (id: string, patch: Partial<ToolCallLink>) => {
    index.set(id, { ...(index.get(id) ?? { name: '' }), ...patch });
  };

  entries.forEach((entry, entryIndex) => {
    for (const block of getMessageBlocks(entry)) {
      if (!isRecord(block)) continue;
      if (block.type === 'tool_use' && typeof block.id === 'string') {
        upsert(block.id, {
          name: getToolUseName(block),
          callIndex: entryIndex,
        });
      }
      if (
        block.type === 'tool_result' &&
        typeof block.tool_use_id === 'string'
      ) {
        upsert(block.tool_use_id, { resultIndex: entryIndex });
      }
    }
  });

  return index;
}

function getFlameEventKind(
  entry: TeamsAgentDebugTrajectoryEntry,
): FlameEventKind {
  if (findContentBlock(entry, 'tool_use')) {
    return 'tool-use';
  }
  if (findContentBlock(entry, 'tool_result')) {
    return 'tool-result';
  }
  if (
    entry.type === 'system' ||
    entry.type === 'user' ||
    entry.type === 'assistant'
  ) {
    return entry.type;
  }
  return 'other';
}

const MAX_LABELLED_TOOL_NAMES = 2;

function getFlameEventLabel(
  entry: TeamsAgentDebugTrajectoryEntry,
  toolLinkIndex: ToolLinkIndex = EMPTY_TOOL_LINK_INDEX,
): string {
  const blocks = getMessageBlocks(entry);
  const toolUses = findContentBlocks(entry, 'tool_use');
  const [firstToolUse] = toolUses;
  if (toolUses.length === 1 && firstToolUse) {
    return `Tool: ${getToolUseName(firstToolUse)}`;
  }
  if (toolUses.length > 1) {
    const names = toolUses.map(getToolUseName);
    const shown = names.slice(0, MAX_LABELLED_TOOL_NAMES).join(', ');
    const remaining = names.length - MAX_LABELLED_TOOL_NAMES;
    return `${names.length.toString()} tools: ${shown}${
      remaining > 0 ? ` +${remaining.toString()} more` : ''
    }`;
  }
  const toolResult = findContentBlock(entry, 'tool_result');
  if (toolResult) {
    const callId =
      typeof toolResult.tool_use_id === 'string' ? toolResult.tool_use_id : '';
    const toolName = callId ? toolLinkIndex.get(callId)?.name : undefined;
    const base = toolName ? `Tool result: ${toolName}` : 'Tool result';
    return entry.isError === true || toolResult.is_error === true
      ? `${base} (error)`
      : base;
  }
  if (entry.subtype.startsWith('session.start')) {
    return 'Session started';
  }
  const textBlock = blocks.find(
    (block): block is Record<string, unknown> =>
      isRecord(block) &&
      block.type === 'text' &&
      typeof block.text === 'string',
  );
  if (
    textBlock &&
    typeof textBlock.text === 'string' &&
    textBlock.text.trimStart().startsWith('# Session Workspace')
  ) {
    return 'Session context';
  }
  if (entry.message?.role === 'user') {
    return 'User query';
  }
  if (entry.message?.role === 'assistant') {
    return 'Assistant response';
  }
  return entry.subtype || getEventLabel(entry);
}

/** The body text of each content block, as rendered under its header. */
function getBlockBodies(entry: TeamsAgentDebugTrajectoryEntry): string[] {
  return getMessageBlocks(entry).map((block) => {
    if (!isRecord(block)) return stringifyValue(block);
    if (block.type === 'text' && typeof block.text === 'string') {
      return block.text;
    }
    if (block.type === 'thinking' && typeof block.thinking === 'string') {
      return block.thinking;
    }
    if (block.type === 'tool_use') return stringifyValue(block.input ?? {});
    if (block.type === 'tool_result') {
      return stringifyValue(block.content ?? '');
    }
    return stringifyValue(block);
  });
}

function getToolCallTurnLabel(entry: TeamsAgentDebugTrajectoryEntry): string {
  return entry.message?.role === 'assistant'
    ? 'Assistant (thinking)'
    : getFlameEventLabel(entry);
}

function isToolResultError(
  entry: TeamsAgentDebugTrajectoryEntry | undefined,
  callId: string,
): boolean {
  if (!entry) return false;
  return findContentBlocks(entry, 'tool_result').some(
    (block) => block.tool_use_id === callId && block.is_error === true,
  );
}

function buildFlameEvents(
  entries: TeamsAgentDebugTrajectoryEntry[],
  toolLinkIndex: ToolLinkIndex,
): FlameEvent[] {
  const timestamped = entries
    .map((entry, index) => ({
      entry,
      index,
      timestampMs: Date.parse(entry.timestamp),
    }))
    .filter((event) => Number.isFinite(event.timestampMs))
    .sort(
      (left, right) =>
        left.timestampMs - right.timestampMs || left.index - right.index,
    );

  if (timestamped.length === 0) return [];

  const startMs = timestamped[0]?.timestampMs ?? 0;
  const timestampByIndex = new Map(
    timestamped.map((event) => [event.index, event.timestampMs]),
  );

  // A tool result is the moment its call finished, not a separate span of work,
  // so it is absorbed into that call's bar rather than getting a row of its own.
  const absorbedResultIndexes = new Set<number>();
  for (const event of timestamped) {
    for (const block of findContentBlocks(event.entry, 'tool_use')) {
      const callId = typeof block.id === 'string' ? block.id : undefined;
      const resultIndex = callId
        ? toolLinkIndex.get(callId)?.resultIndex
        : undefined;
      if (resultIndex !== undefined) absorbedResultIndexes.add(resultIndex);
    }
  }

  const precedingTimestamps = new Map<number, number | undefined>();
  let precedingTimestamp: number | undefined;
  for (const event of timestamped) {
    precedingTimestamps.set(event.index, precedingTimestamp);
    // Separate messages can issue concurrent calls. A call's emission is not
    // completed work that starts another thinking interval; a result is.
    if (findContentBlocks(event.entry, 'tool_use').length === 0) {
      precedingTimestamp = event.timestampMs;
    }
  }

  // Assistant messages that each emit calls from the same baseline came from
  // one processing interval, so they share a single thinking bar, as if the
  // calls had been emitted together in one message.
  const thinkingGroups = new Map<number | undefined, number[]>();
  const thinkingGroupByIndex = new Map<number, number[]>();
  for (const event of timestamped) {
    if (
      event.entry.message?.role !== 'assistant' ||
      findContentBlocks(event.entry, 'tool_use').length === 0
    ) {
      continue;
    }
    const baseline = precedingTimestamps.get(event.index);
    const group = thinkingGroups.get(baseline) ?? [];
    group.push(event.index);
    thinkingGroups.set(baseline, group);
    thinkingGroupByIndex.set(event.index, group);
  }
  const getResultIndexes = (index: number): number[] => {
    const entry = entries[index];
    if (!entry) return [];
    return findContentBlocks(entry, 'tool_use').flatMap((block) => {
      const resultIndex =
        typeof block.id === 'string'
          ? toolLinkIndex.get(block.id)?.resultIndex
          : undefined;
      return resultIndex === undefined ? [] : [resultIndex];
    });
  };

  const events = timestamped.flatMap((event, position) => {
    if (absorbedResultIndexes.has(event.index)) return [];

    const toolUses = findContentBlocks(event.entry, 'tool_use');
    const previousTimestamp =
      toolUses.length > 0
        ? precedingTimestamps.get(event.index)
        : timestamped[position - 1]?.timestampMs;
    const nextTimestamp = timestamped[position + 1]?.timestampMs;
    const fallbackDurationMs =
      nextTimestamp === undefined
        ? 0
        : Math.max(0, nextTimestamp - event.timestampMs);
    const shared = {
      ...event,
      isError:
        event.entry.isError === true ||
        findContentBlock(event.entry, 'tool_result')?.is_error === true,
    };

    // An event's timestamp is when it was emitted, so its bar runs backwards to
    // the previous event and measures the work that produced it. The first
    // event has nothing before it and renders as a marker.
    const precedingDurationMs =
      previousTimestamp === undefined
        ? 0
        : Math.max(0, event.timestampMs - previousTimestamp);
    const precedingOffsetMs =
      (previousTimestamp ?? event.timestampMs) - startMs;

    if (toolUses.length > 0) {
      // The turn that issued the calls keeps a bar for its own generation time,
      // then each call spans forward from that turn to its own result, so
      // parallel calls line up as concurrent bars.
      const turn: FlameEvent = {
        ...shared,
        key: event.index.toString(),
        label: getToolCallTurnLabel(event.entry),
        kind:
          event.entry.message?.role === 'assistant'
            ? 'assistant'
            : getFlameEventKind(event.entry),
        offsetMs: precedingOffsetMs,
        durationMs: precedingDurationMs,
      };

      const calls = toolUses.map((block, toolPosition) => {
        const callId = typeof block.id === 'string' ? block.id : undefined;
        const resultIndex = callId
          ? toolLinkIndex.get(callId)?.resultIndex
          : undefined;
        const resultTimestampMs =
          resultIndex === undefined
            ? undefined
            : timestampByIndex.get(resultIndex);
        return {
          ...shared,
          key: `${event.index.toString()}:tool:${toolPosition.toString()}`,
          label: `Tool: ${getToolUseName(block)}`,
          kind: 'tool-use' as const,
          // The bar ends on the result, so a failed result marks the bar.
          isError:
            shared.isError ||
            (callId !== undefined &&
              resultIndex !== undefined &&
              isToolResultError(entries[resultIndex], callId)),
          offsetMs: event.timestampMs - startMs,
          durationMs:
            resultTimestampMs === undefined
              ? fallbackDurationMs
              : Math.max(0, resultTimestampMs - event.timestampMs),
          linkedIndexes: resultIndex === undefined ? undefined : [resultIndex],
        };
      });

      // Every row of a turn shares the turn's index, so the timeline highlights
      // the whole group; selecting any of its bars shows all of its results
      // and the other messages that share its thinking bar.
      const thinkingGroup = thinkingGroupByIndex.get(event.index) ?? [
        event.index,
      ];
      const groupResults = [
        ...thinkingGroup.filter((index) => index !== event.index),
        ...thinkingGroup.flatMap(getResultIndexes),
      ];
      const relatedIndexes = groupResults.length > 0 ? groupResults : undefined;
      const callRows = calls.map((call) => ({ ...call, relatedIndexes }));
      if (event.index !== Math.min(...thinkingGroup)) return callRows;
      const groupEndMs = Math.max(
        ...thinkingGroup.map(
          (index) => timestampByIndex.get(index) ?? event.timestampMs,
        ),
      );
      return [
        {
          ...turn,
          durationMs:
            previousTimestamp === undefined
              ? 0
              : Math.max(0, groupEndMs - previousTimestamp),
          relatedIndexes,
        },
        ...callRows,
      ];
    }

    return [
      {
        ...shared,
        key: event.index.toString(),
        label: getFlameEventLabel(event.entry, toolLinkIndex),
        kind: getFlameEventKind(event.entry),
        offsetMs: precedingOffsetMs,
        durationMs: precedingDurationMs,
      },
    ];
  });
  // Sequence governs row order; timestamps govern geometry, independently.
  return events.sort((left, right) => left.index - right.index);
}

function formatDuration(durationMs: number): string {
  if (durationMs < 1000) return `${Math.round(durationMs)}ms`;
  if (durationMs < 60_000) return `${(durationMs / 1000).toFixed(2)}s`;
  const minutes = Math.floor(durationMs / 60_000);
  const seconds = ((durationMs % 60_000) / 1000).toFixed(1);
  return `${minutes}m ${seconds}s`;
}

function formatAxisDuration(durationMs: number): string {
  if (durationMs < 1000) return `${Math.round(durationMs)}ms`;
  const totalSeconds = Math.round(durationMs / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  return `${minutes}m ${totalSeconds % 60}s`;
}

const FLAME_TICK_RATIOS = [0, 0.25, 0.5, 0.75, 1];
const FLAME_END_PADDING_RATIO = 0.2;
const RANGE_SELECTION_THRESHOLD_PX = 4;
const OVERVIEW_MIN_BAR_WIDTH_PERCENT = 0.4;
const FLAME_BAR_MIN_LABEL_WIDTH_PERCENT = 1;
const OVERVIEW_LANE_HEIGHT_PX = 10;

interface UsageStat {
  label: string;
  value: string;
}

interface RowSelectionModifiers {
  range: boolean;
  toggle: boolean;
}

interface TimelineViewRange {
  startMs: number;
  endMs: number;
}

interface TimelineDragRange {
  pointerId: number;
  startX: number;
  currentX: number;
  surfaceLeft: number;
  surfaceWidth: number;
}

interface TimelineResize {
  pointerId: number;
  side: 'start' | 'end';
  fixedMs: number;
  surfaceLeft: number;
  surfaceWidth: number;
}

type FlameEventFilter =
  | 'all'
  | 'tools'
  | 'user'
  | 'assistant'
  | 'system'
  | 'other';

const FLAME_EVENT_FILTERS: {
  label: string;
  value: FlameEventFilter;
}[] = [
  { label: 'All', value: 'all' },
  { label: 'Tools', value: 'tools' },
  { label: 'User', value: 'user' },
  { label: 'Assistant', value: 'assistant' },
  { label: 'System', value: 'system' },
  { label: 'Other', value: 'other' },
];

function matchesFlameEventFilter(
  event: FlameEvent,
  filter: FlameEventFilter,
): boolean {
  if (filter === 'all') return true;
  if (filter === 'tools') {
    return event.kind === 'tool-use' || event.kind === 'tool-result';
  }
  return event.kind === filter;
}

function isFlameEventInRange(
  event: FlameEvent,
  startMs: number,
  endMs: number,
): boolean {
  if (event.durationMs === 0) {
    return event.offsetMs >= startMs && event.offsetMs <= endMs;
  }

  const eventEndMs = event.offsetMs + event.durationMs;
  return eventEndMs > startMs && event.offsetMs < endMs;
}

function buildOverviewEvents(events: FlameEvent[], extendedDurationMs: number) {
  const laneEnds: number[] = [];
  return events.map((event) => {
    const left = (event.offsetMs / extendedDurationMs) * 100;
    const width = Math.max(
      (event.durationMs / extendedDurationMs) * 100,
      OVERVIEW_MIN_BAR_WIDTH_PERCENT,
    );
    const end = Math.min(100, left + width);
    let lane = laneEnds.findIndex((laneEnd) => left >= laneEnd);
    if (lane === -1) {
      lane = laneEnds.length;
      laneEnds.push(end);
    } else {
      laneEnds[lane] = end;
    }
    return { event, lane, left, width: end - left };
  });
}

const USAGE_LABELS: Record<string, string> = {
  input_tokens: 'Input tokens',
  output_tokens: 'Output tokens',
  cache_read_input_tokens: 'Cache read tokens',
  cache_creation_input_tokens: 'Cache creation tokens',
};

function getUsageStats(entry: TeamsAgentDebugTrajectoryEntry): UsageStat[] {
  if (!entry.message?.usage) return [];
  return Object.entries(entry.message.usage)
    .filter((usageEntry): usageEntry is [string, string | number] => {
      const value = usageEntry[1];
      return typeof value === 'number' || typeof value === 'string';
    })
    .map(([key, value]) => ({
      label:
        USAGE_LABELS[key] ??
        key
          .split('_')
          .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
          .join(' '),
      value: typeof value === 'number' ? value.toLocaleString('en-US') : value,
    }));
}

function EventUsage({ entry }: { entry: TeamsAgentDebugTrajectoryEntry }) {
  const styles = useStyles();
  const usageStats = getUsageStats(entry);
  if (usageStats.length === 0) return null;

  return (
    <section aria-label="Usage statistics" className={styles.eventUsage}>
      <Text className={styles.eventUsageTitle}>Usage</Text>
      <dl className={styles.eventUsageGrid}>
        {usageStats.map((stat) => (
          <Fragment key={stat.label}>
            <dt className={styles.eventUsageKey}>{stat.label}</dt>
            <dd className={styles.eventUsageValue}>{stat.value}</dd>
          </Fragment>
        ))}
      </dl>
    </section>
  );
}

function getFlameEventDescription(event: FlameEvent): string {
  const usage = getUsageStats(event.entry)
    .map((stat) => `${stat.label.toLowerCase()} ${stat.value}`)
    .join(', ');
  return `${event.label} start ${formatDuration(
    event.offsetMs,
  )}, duration ${formatDuration(event.durationMs)}${usage ? `, ${usage}` : ''}`;
}

function DebugTrajectoryFlameTimeline({
  entries,
  highlightedIndex,
  selectedIndices,
  onClearSelection,
  onOpenEvent,
  onSelectRow,
  onVisibleEventsChange,
}: {
  entries: TeamsAgentDebugTrajectoryEntry[];
  highlightedIndex: number | null;
  selectedIndices: ReadonlySet<number>;
  onClearSelection: () => void;
  onOpenEvent: (index: number, linkedIndexes?: readonly number[]) => void;
  onSelectRow: (
    index: number,
    modifiers: RowSelectionModifiers,
    linkedIndexes?: readonly number[],
  ) => void;
  onVisibleEventsChange: (indices: ReadonlySet<number> | null) => void;
}) {
  const styles = useStyles();
  const [viewRange, setViewRange] = useState<TimelineViewRange | null>(null);
  const [eventFilter, setEventFilter] = useState<FlameEventFilter>('all');
  const [dragRange, setDragRange] = useState<TimelineDragRange | null>(null);
  const dragRangeRef = useRef<TimelineDragRange | null>(null);
  const resizeRef = useRef<TimelineResize | null>(null);
  const { index: toolLinkIndex } = useContext(ToolLinkContext);
  const events = buildFlameEvents(entries, toolLinkIndex);

  const totalDurationMs = Math.max(
    1,
    events.reduce(
      (longest, event) => Math.max(longest, event.offsetMs + event.durationMs),
      0,
    ),
  );
  const extendedDurationMs = totalDurationMs * (1 + FLAME_END_PADDING_RATIO);
  const viewStartMs = viewRange?.startMs ?? 0;
  const viewEndMs = viewRange?.endMs ?? extendedDurationMs;
  const viewDurationMs = Math.max(1, viewEndMs - viewStartMs);

  const barClassByKind: Record<FlameEventKind, string> = {
    system: styles.flameBarSystem,
    user: styles.flameBarUser,
    assistant: styles.flameBarAssistant,
    'tool-use': styles.flameBarToolUse,
    'tool-result': styles.flameBarToolResult,
    other: styles.flameBarOther,
  };

  const getViewPercentage = (timeMs: number) =>
    ((timeMs - viewStartMs) / viewDurationMs) * 100;

  const updateVisibleEvents = (
    range: TimelineViewRange | null,
    filter: FlameEventFilter,
  ) => {
    onVisibleEventsChange(
      range === null && filter === 'all'
        ? null
        : new Set(
            events
              .filter((event) => {
                if (!matchesFlameEventFilter(event, filter)) return false;
                if (range === null) return true;
                return isFlameEventInRange(event, range.startMs, range.endMs);
              })
              .flatMap((event) => [
                event.index,
                ...(event.linkedIndexes ?? []),
              ]),
          ),
    );
  };

  const updateViewRange = (range: TimelineViewRange | null) => {
    const fullRangeThresholdMs = extendedDurationMs * 0.0001;
    const normalizedRange =
      range !== null &&
      range.startMs <= fullRangeThresholdMs &&
      range.endMs >= extendedDurationMs - fullRangeThresholdMs
        ? null
        : range;
    setViewRange(normalizedRange);
    updateVisibleEvents(normalizedRange, eventFilter);
  };

  const updateEventFilter = (filter: FlameEventFilter) => {
    setEventFilter(filter);
    updateVisibleEvents(viewRange, filter);
    onClearSelection();
  };

  useEffect(() => {
    setViewRange(null);
    setEventFilter('all');
    setDragRange(null);
    dragRangeRef.current = null;
    resizeRef.current = null;
    onVisibleEventsChange(null);
  }, [entries, onVisibleEventsChange]);

  const getDragWindowStyle = () => {
    if (!dragRange) return undefined;
    const startRatio = Math.min(dragRange.startX, dragRange.currentX);
    const endRatio = Math.max(dragRange.startX, dragRange.currentX);
    return {
      left: `${((startRatio - dragRange.surfaceLeft) / dragRange.surfaceWidth) * 100}%`,
      width: `${((endRatio - startRatio) / dragRange.surfaceWidth) * 100}%`,
    };
  };

  const handleRangePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (
      event.button !== 0 ||
      (event.target as HTMLElement).closest('[data-timeline-event-bar]')
    ) {
      return;
    }

    const surface = event.currentTarget;
    const bounds = surface.getBoundingClientRect();
    const nextDragRange = {
      pointerId: event.pointerId,
      startX: event.clientX,
      currentX: event.clientX,
      surfaceLeft: bounds.left,
      surfaceWidth: Math.max(1, bounds.width),
    };
    surface.setPointerCapture?.(event.pointerId);
    dragRangeRef.current = nextDragRange;
    setDragRange(nextDragRange);
  };

  const handleRangePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const current = dragRangeRef.current;
    if (current?.pointerId !== event.pointerId) return;

    const nextDragRange = {
      ...current,
      currentX: Math.min(
        current.surfaceLeft + current.surfaceWidth,
        Math.max(current.surfaceLeft, event.clientX),
      ),
    };
    dragRangeRef.current = nextDragRange;
    setDragRange(nextDragRange);
  };

  const finishRangeSelection = (event: ReactPointerEvent<HTMLDivElement>) => {
    const current = dragRangeRef.current;
    if (current === null) return;
    if (current.pointerId !== event.pointerId) return;

    const moved = Math.abs(current.currentX - current.startX);
    if (moved >= RANGE_SELECTION_THRESHOLD_PX) {
      const rangeStartX = Math.min(current.startX, current.currentX);
      const rangeEndX = Math.max(current.startX, current.currentX);
      const startRatio =
        (rangeStartX - current.surfaceLeft) / current.surfaceWidth;
      const endRatio = (rangeEndX - current.surfaceLeft) / current.surfaceWidth;
      updateViewRange({
        startMs: viewStartMs + viewDurationMs * startRatio,
        endMs: viewStartMs + viewDurationMs * endRatio,
      });
    } else {
      onClearSelection();
    }

    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
    dragRangeRef.current = null;
    setDragRange(null);
  };

  const cancelRangeSelection = (event: ReactPointerEvent<HTMLDivElement>) => {
    const current = dragRangeRef.current;
    if (current === null) return;
    if (current.pointerId !== event.pointerId) return;

    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
    dragRangeRef.current = null;
    setDragRange(null);
  };

  const handleResizePointerDown = (
    event: ReactPointerEvent<HTMLButtonElement>,
    side: TimelineResize['side'],
  ) => {
    if (event.button !== 0) return;

    event.stopPropagation();
    const navigator = event.currentTarget.parentElement;
    if (!navigator) return;
    const bounds = navigator.getBoundingClientRect();
    resizeRef.current = {
      pointerId: event.pointerId,
      side,
      fixedMs: side === 'start' ? viewEndMs : viewStartMs,
      surfaceLeft: bounds.left,
      surfaceWidth: Math.max(1, bounds.width),
    };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const handleResizePointerMove = (
    event: ReactPointerEvent<HTMLButtonElement>,
  ) => {
    const resize = resizeRef.current;
    if (resize?.pointerId !== event.pointerId) return;

    const ratio = Math.min(
      1,
      Math.max(0, (event.clientX - resize.surfaceLeft) / resize.surfaceWidth),
    );
    const pointerMs = ratio * extendedDurationMs;
    resizeViewRange(resize.side, resize.fixedMs, pointerMs);
  };

  const resizeViewRange = (
    side: TimelineResize['side'],
    fixedMs: number,
    requestedMs: number,
  ) => {
    const minimumRangeMs = extendedDurationMs * 0.01;
    if (side === 'start') {
      const endMs = Math.max(minimumRangeMs, fixedMs);
      updateViewRange({
        startMs: Math.max(0, Math.min(requestedMs, endMs - minimumRangeMs)),
        endMs,
      });
      return;
    }

    const startMs = Math.min(extendedDurationMs - minimumRangeMs, fixedMs);
    updateViewRange({
      startMs,
      endMs: Math.min(
        extendedDurationMs,
        Math.max(requestedMs, startMs + minimumRangeMs),
      ),
    });
  };

  const finishResize = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (resizeRef.current?.pointerId !== event.pointerId) return;

    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture?.(event.pointerId);
    }
    resizeRef.current = null;
  };

  const handleResizeKeyDown = (
    event: ReactKeyboardEvent<HTMLButtonElement>,
    side: TimelineResize['side'],
  ) => {
    const stepMs = extendedDurationMs * 0.01;
    const currentMs = side === 'start' ? viewStartMs : viewEndMs;
    const fixedMs = side === 'start' ? viewEndMs : viewStartMs;
    let requestedMs: number | null = null;

    if (event.key === 'ArrowLeft' || event.key === 'ArrowDown') {
      requestedMs = currentMs - stepMs;
    } else if (event.key === 'ArrowRight' || event.key === 'ArrowUp') {
      requestedMs = currentMs + stepMs;
    } else if (event.key === 'Home') {
      requestedMs = 0;
    } else if (event.key === 'End') {
      requestedMs = extendedDurationMs;
    }

    if (requestedMs === null) return;
    event.preventDefault();
    resizeViewRange(side, fixedMs, requestedMs);
  };

  if (events.length === 0) return null;

  const dragWindowStyle = getDragWindowStyle();
  const overviewStart = (viewStartMs / extendedDurationMs) * 100;
  const overviewWidth = (viewDurationMs / extendedDurationMs) * 100;
  const overviewEvents = buildOverviewEvents(events, extendedDurationMs);
  const overviewLaneCount =
    Math.max(0, ...overviewEvents.map((item) => item.lane)) + 1;
  const filteredEvents = events.filter((event) =>
    matchesFlameEventFilter(event, eventFilter),
  );

  return (
    <Card className={styles.flameCard}>
      <div className={styles.flameHeader}>
        <Text className={styles.flameTitle}>Interaction Latency</Text>
        <div className={styles.flameHeaderActions}>
          <Text className={styles.flameHint}>
            Total {formatDuration(totalDurationMs)}
          </Text>
          <Text className={styles.zoomLabel}>
            Drag across the timeline to zoom
          </Text>
        </div>
      </div>
      <div className={styles.flameToolbar}>
        <div
          aria-label="Timeline event filters"
          className={styles.flameFilters}
        >
          {FLAME_EVENT_FILTERS.map((filter) => (
            <button
              aria-pressed={eventFilter === filter.value}
              className={mergeClasses(
                styles.flameFilterButton,
                eventFilter === filter.value &&
                  styles.flameFilterButtonSelected,
              )}
              key={filter.value}
              onClick={() => updateEventFilter(filter.value)}
              type="button"
            >
              {filter.label}
            </button>
          ))}
        </div>
        {viewRange && (
          <button
            className={styles.resetZoomButton}
            onClick={() => updateViewRange(null)}
            type="button"
          >
            Reset zoom
          </button>
        )}
      </div>
      <div className={styles.zoomOverview}>
        <div
          aria-label="Full timeline zoom navigator"
          className={styles.zoomNavigator}
          role="region"
          style={{
            height: `${Math.max(
              38,
              overviewLaneCount * OVERVIEW_LANE_HEIGHT_PX + 12,
            )}px`,
          }}
        >
          {overviewEvents.map(({ event, lane, left, width }) => (
            <span
              aria-hidden
              className={mergeClasses(
                styles.zoomNavigatorBar,
                event.isError
                  ? styles.flameBarError
                  : barClassByKind[event.kind],
              )}
              data-overview-event
              data-overview-event-error={event.isError || undefined}
              key={event.key}
              style={{
                left: `${left}%`,
                top: `${6 + lane * OVERVIEW_LANE_HEIGHT_PX}px`,
                width: `${width}%`,
              }}
            />
          ))}
          <span
            aria-hidden
            className={styles.zoomNavigatorShade}
            style={{ left: 0, width: `${overviewStart}%` }}
          />
          <span
            aria-hidden
            className={styles.zoomNavigatorShade}
            style={{
              left: `${overviewStart + overviewWidth}%`,
              right: 0,
            }}
          />
          <span
            aria-hidden
            className={styles.zoomNavigatorWindow}
            style={{
              left: `${overviewStart}%`,
              width: `${overviewWidth}%`,
            }}
          />
          <button
            aria-label="Adjust zoom start"
            aria-valuemax={Math.round(extendedDurationMs)}
            aria-valuemin={0}
            aria-valuenow={Math.round(viewStartMs)}
            aria-valuetext={formatDuration(viewStartMs)}
            className={styles.zoomNavigatorHandle}
            onKeyDown={(event) => handleResizeKeyDown(event, 'start')}
            onPointerCancel={finishResize}
            onPointerDown={(event) => handleResizePointerDown(event, 'start')}
            onPointerMove={handleResizePointerMove}
            onPointerUp={finishResize}
            style={{ left: `${overviewStart}%` }}
            role="slider"
            type="button"
          />
          <button
            aria-label="Adjust zoom end"
            aria-valuemax={Math.round(extendedDurationMs)}
            aria-valuemin={0}
            aria-valuenow={Math.round(viewEndMs)}
            aria-valuetext={formatDuration(viewEndMs)}
            className={styles.zoomNavigatorHandle}
            onKeyDown={(event) => handleResizeKeyDown(event, 'end')}
            onPointerCancel={finishResize}
            onPointerDown={(event) => handleResizePointerDown(event, 'end')}
            onPointerMove={handleResizePointerMove}
            onPointerUp={finishResize}
            style={{ left: `${overviewStart + overviewWidth}%` }}
            role="slider"
            type="button"
          />
        </div>
      </div>
      <div
        aria-label="Interaction latency timeline, drag horizontally to zoom"
        className={styles.flameViewport}
        role="region"
      >
        <div className={styles.flameGrid}>
          <button
            aria-label="Show all timeline events"
            aria-pressed={selectedIndices.size === 0}
            className={styles.flameAxisLabel}
            data-timeline-row-label
            onClick={onClearSelection}
            type="button"
          >
            Event
          </button>
          <div
            className={styles.flameAxis}
            onPointerCancel={cancelRangeSelection}
            onPointerDown={handleRangePointerDown}
            onPointerMove={handleRangePointerMove}
            onPointerUp={finishRangeSelection}
          >
            {FLAME_TICK_RATIOS.map((ratio) => (
              <div
                className={styles.flameTick}
                key={ratio}
                style={{ left: `${ratio * 100}%` }}
              >
                <span
                  className={mergeClasses(
                    styles.flameTickLabel,
                    ratio === 1 && styles.flameTickLabelLast,
                  )}
                >
                  {formatAxisDuration(viewStartMs + viewDurationMs * ratio)}
                </span>
              </div>
            ))}
            {dragWindowStyle && (
              <span
                aria-hidden
                className={styles.flameSelectionWindow}
                style={dragWindowStyle}
              />
            )}
          </div>

          {filteredEvents.map((event) => {
            const eventEndMs = event.offsetMs + event.durationMs;
            const visible = isFlameEventInRange(event, viewStartMs, viewEndMs);
            const visibleStartMs = Math.max(event.offsetMs, viewStartMs);
            const visibleEndMs = Math.min(eventEndMs, viewEndMs);
            const left = getViewPercentage(visibleStartMs);
            const width = getViewPercentage(visibleEndMs) - left;
            const description = getFlameEventDescription(event);
            const start = formatDuration(event.offsetMs);
            const duration = formatDuration(event.durationMs);
            const usageStats = getUsageStats(event.entry);
            const selected = selectedIndices.has(event.index);
            const highlighted =
              selected ||
              (selectedIndices.size === 0 && highlightedIndex === event.index);

            return (
              <Fragment key={event.key}>
                <Tooltip
                  content={event.label}
                  relationship="description"
                  positioning="above-start"
                >
                  <button
                    aria-label={`Select ${event.label} timeline row label${
                      visible ? '' : ', outside zoom range'
                    }`}
                    aria-pressed={selected}
                    className={mergeClasses(
                      styles.flameLaneLabel,
                      highlighted && styles.flameLaneLabelSelected,
                      !visible && styles.flameRowOutsideZoom,
                    )}
                    onClick={(clickEvent) =>
                      onSelectRow(
                        event.index,
                        {
                          range: clickEvent.shiftKey,
                          toggle: clickEvent.ctrlKey || clickEvent.metaKey,
                        },
                        getSelectionGroup(event),
                      )
                    }
                    data-timeline-row-label
                    type="button"
                  >
                    {event.label}
                  </button>
                </Tooltip>
                <div
                  className={mergeClasses(
                    styles.flameLane,
                    highlighted && styles.flameLaneSelected,
                    !visible && styles.flameRowOutsideZoom,
                  )}
                  onPointerCancel={cancelRangeSelection}
                  onPointerDown={handleRangePointerDown}
                  onPointerMove={handleRangePointerMove}
                  onPointerUp={finishRangeSelection}
                >
                  {FLAME_TICK_RATIOS.map((ratio) => (
                    <span
                      className={styles.flameGridLine}
                      key={ratio}
                      style={{ left: `${ratio * 100}%` }}
                    />
                  ))}
                  {dragWindowStyle && (
                    <span
                      aria-hidden
                      className={styles.flameSelectionWindow}
                      style={dragWindowStyle}
                    />
                  )}
                  {visible && (
                    <Tooltip
                      content={{
                        children: (
                          <div className={styles.flameTooltip}>
                            <div className={styles.flameTooltipLabel}>
                              {event.label}
                            </div>
                            <div className={styles.flameTooltipTiming}>
                              <span className={styles.flameTooltipKey}>
                                Start
                              </span>
                              <span>{start}</span>
                              <span className={styles.flameTooltipKey}>
                                Duration
                              </span>
                              <span>{duration}</span>
                            </div>
                            {usageStats.length > 0 && (
                              <>
                                <div className={styles.flameTooltipSection}>
                                  Usage
                                </div>
                                <div className={styles.flameTooltipTiming}>
                                  {usageStats.map((stat) => (
                                    <Fragment key={stat.label}>
                                      <span className={styles.flameTooltipKey}>
                                        {stat.label}
                                      </span>
                                      <span>{stat.value}</span>
                                    </Fragment>
                                  ))}
                                </div>
                              </>
                            )}
                          </div>
                        ),
                      }}
                      relationship="description"
                    >
                      <button
                        aria-label={description}
                        aria-current={highlighted ? 'true' : undefined}
                        aria-pressed={selected}
                        className={mergeClasses(
                          styles.flameBar,
                          width < FLAME_BAR_MIN_LABEL_WIDTH_PERCENT &&
                            styles.flameBarThin,
                          highlighted && styles.flameBarSelected,
                          event.isError
                            ? styles.flameBarError
                            : barClassByKind[event.kind],
                        )}
                        onClick={(clickEvent) => {
                          clickEvent.stopPropagation();
                          onOpenEvent(event.index, getSelectionGroup(event));
                        }}
                        data-timeline-event-bar
                        data-timeline-event-error={event.isError || undefined}
                        style={
                          {
                            [FLAME_BAR_LEFT_VAR]: `${left}%`,
                            [FLAME_BAR_END_VAR]: `${left + width}%`,
                          } as CSSProperties
                        }
                        type="button"
                      >
                        {event.durationMs > 0 &&
                        width >= FLAME_BAR_MIN_LABEL_WIDTH_PERCENT
                          ? event.label
                          : ''}
                      </button>
                    </Tooltip>
                  )}
                </div>
              </Fragment>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

const COLLAPSED_BODY_LINE_LIMIT = 4;
const ESTIMATED_CHARACTERS_PER_LINE = 120;

function estimateLineCount(text: string): number {
  return text
    .split(/\r?\n/)
    .reduce(
      (lineCount, line) =>
        lineCount +
        Math.max(1, Math.ceil(line.length / ESTIMATED_CHARACTERS_PER_LINE)),
      0,
    );
}

function DebugTrajectoryTimelineEvent({
  entry,
  eventId,
  expandRequest,
  index,
  isLast,
  selected,
  onClearSelection,
  onHighlight,
}: {
  entry: TeamsAgentDebugTrajectoryEntry;
  eventId: string;
  expandRequest: number;
  index: number;
  isLast: boolean;
  selected: boolean;
  onClearSelection: () => void;
  onHighlight: (index: number) => void;
}) {
  const styles = useStyles();
  const contentId = useId();
  const { index: toolLinkIndex } = useContext(ToolLinkContext);
  const eventLabel = getEventLabel(entry);
  const eventDescription = getFlameEventLabel(entry, toolLinkIndex);
  // Only worth collapsing when clamping would actually hide some text.
  const isCollapsible = getBlockBodies(entry).some(
    (body) => estimateLineCount(body) > COLLAPSED_BODY_LINE_LIMIT,
  );
  const [expanded, setExpanded] = useState(!isCollapsible);

  useEffect(() => {
    if (expandRequest > 0 && isCollapsible) {
      setExpanded(true);
    }
  }, [expandRequest, isCollapsible]);

  const headerContent = (
    <>
      {isCollapsible && (
        <span className={styles.chevron}>
          {expanded ? <ChevronDown16Regular /> : <ChevronRight16Regular />}
        </span>
      )}
      <Badge appearance="filled" color="brand">
        {eventLabel}
      </Badge>
      <Text className={styles.timestamp}>
        {formatTimestamp(entry.timestamp)}
      </Text>
      {entry.subtype && <Text className={styles.subtype}>{entry.subtype}</Text>}
    </>
  );

  return (
    <div
      className={styles.event}
      id={eventId}
      aria-label={`${eventDescription} event details`}
      onClickCapture={(event) => {
        if (
          (event.target as HTMLElement).closest('[data-trajectory-event-card]')
        ) {
          onHighlight(index);
          return;
        }
        onClearSelection();
      }}
      onFocusCapture={() => onHighlight(index)}
      role="article"
    >
      <div
        className={isLast ? `${styles.rail} ${styles.railLast}` : styles.rail}
      >
        <span className={styles.dot} />
      </div>
      <Card
        className={mergeClasses(styles.card, selected && styles.cardSelected)}
        data-trajectory-event-card
      >
        {isCollapsible ? (
          <button
            type="button"
            className={styles.headerButton}
            aria-expanded={expanded}
            aria-controls={contentId}
            aria-label={`${expanded ? 'Collapse' : 'Expand'} ${eventDescription} event`}
            onClick={() => setExpanded((current) => !current)}
          >
            {headerContent}
          </button>
        ) : (
          <div className={styles.staticHeader}>{headerContent}</div>
        )}
        <div className={styles.eventContent} id={contentId}>
          <EventContent
            collapsed={!expanded}
            entry={entry}
            entryIndex={index}
          />
        </div>
        <EventUsage entry={entry} />
      </Card>
    </div>
  );
}

export function DebugTrajectoryTimeline({
  payload,
}: DebugTrajectoryTimelineProps) {
  const styles = useStyles();
  const eventIdPrefix = useId().replace(/:/g, '');
  const [selectedIndices, setSelectedIndices] = useState<Set<number>>(
    () => new Set(),
  );
  const [highlightedIndex, setHighlightedIndex] = useState<number | null>(null);
  const [timelineVisibleIndices, setTimelineVisibleIndices] =
    useState<ReadonlySet<number> | null>(null);
  const [expandRequest, setExpandRequest] = useState({
    index: -1,
    version: 0,
  });
  const [pendingScrollIndex, setPendingScrollIndex] = useState<number | null>(
    null,
  );
  // Remounts the flame timeline so its internal filter and zoom reset too.
  const [flameResetVersion, setFlameResetVersion] = useState(0);
  const selectionAnchorRef = useRef<number | null>(null);

  useEffect(() => {
    selectionAnchorRef.current = null;
    setSelectedIndices(new Set());
    setHighlightedIndex(null);
    setTimelineVisibleIndices(null);
    setExpandRequest((current) => ({
      index: -1,
      version: current.version + 1,
    }));
  }, [payload.entries]);

  const getEventId = useCallback(
    (index: number) => `${eventIdPrefix}-event-${index.toString()}`,
    [eventIdPrefix],
  );

  const handleTimelineSelect = useCallback(
    (index: number, linkedIndexes: readonly number[] = []) => {
      // A tool call's bar ends on its result, so opening it shows both.
      setSelectedIndices(new Set([index, ...linkedIndexes]));
      setHighlightedIndex(null);
      selectionAnchorRef.current = index;
      setExpandRequest((current) => ({
        index,
        version: current.version + 1,
      }));
      document
        .getElementById(getEventId(index))
        ?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    },
    [getEventId],
  );

  const handleRowSelect = useCallback(
    (
      index: number,
      modifiers: RowSelectionModifiers,
      linkedIndexes: readonly number[] = [],
    ) => {
      setHighlightedIndex(null);
      if (modifiers.range && selectionAnchorRef.current !== null) {
        const start = Math.min(selectionAnchorRef.current, index);
        const end = Math.max(selectionAnchorRef.current, index);
        setSelectedIndices(
          new Set(
            Array.from(
              { length: end - start + 1 },
              (_value, offset) => start + offset,
            ),
          ),
        );
        return;
      }

      // Selecting a tool call also selects the result it ends on, since the
      // two share one bar on the timeline.
      const group = [index, ...linkedIndexes];
      selectionAnchorRef.current = index;
      setSelectedIndices((current) => {
        if (!modifiers.toggle) {
          if (current.has(index)) {
            selectionAnchorRef.current = null;
            return new Set();
          }
          return new Set(group);
        }

        const next = new Set(current);
        if (next.has(index)) {
          for (const member of group) next.delete(member);
        } else {
          for (const member of group) next.add(member);
        }
        return next;
      });
    },
    [],
  );

  const handleClearSelection = useCallback(() => {
    selectionAnchorRef.current = null;
    setSelectedIndices(new Set());
    setHighlightedIndex(null);
  }, []);

  /**
   * Jump to the event that a tool call or tool result is joined to. Filters are
   * cleared first so the linked event is rendered even when it sits outside the
   * current selection or zoom window.
   */
  const handleNavigateToEvent = useCallback((index: number) => {
    selectionAnchorRef.current = null;
    setFlameResetVersion((current) => current + 1);
    setSelectedIndices(new Set());
    setTimelineVisibleIndices(null);
    setHighlightedIndex(index);
    setExpandRequest((current) => ({
      index,
      version: current.version + 1,
    }));
    setPendingScrollIndex(index);
  }, []);

  useEffect(() => {
    if (pendingScrollIndex === null) return;
    document
      .getElementById(getEventId(pendingScrollIndex))
      ?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
    setPendingScrollIndex(null);
  }, [getEventId, pendingScrollIndex]);

  const toolLinkIndex = useMemo(
    () => buildToolLinkIndex(payload.entries),
    [payload.entries],
  );

  const toolLinkContextValue = useMemo(
    () => ({ index: toolLinkIndex, onNavigate: handleNavigateToEvent }),
    [handleNavigateToEvent, toolLinkIndex],
  );

  const visibleEntries = useMemo(
    () =>
      payload.entries
        .map((entry, index) => ({ entry, index }))
        .filter(
          ({ index }) =>
            (selectedIndices.size === 0 || selectedIndices.has(index)) &&
            (timelineVisibleIndices === null ||
              timelineVisibleIndices.has(index)),
        ),
    [payload.entries, selectedIndices, timelineVisibleIndices],
  );

  return (
    <ToolLinkContext.Provider value={toolLinkContextValue}>
      <div className={styles.root}>
        <div className={styles.summary}>
          <Badge appearance="tint" color="informative">
            {payload.entryCount} events
          </Badge>
          {payload.source && (
            <Badge appearance="outline" color="informative">
              {payload.source}
            </Badge>
          )}
          {payload.conversationId && (
            <Text className={styles.conversationId}>
              {payload.conversationId}
            </Text>
          )}
        </div>

        {payload.entries.length === 0 ? (
          <div className={styles.empty}>No trajectory events were found.</div>
        ) : (
          <>
            <DebugTrajectoryFlameTimeline
              key={flameResetVersion}
              entries={payload.entries}
              highlightedIndex={highlightedIndex}
              selectedIndices={selectedIndices}
              onClearSelection={handleClearSelection}
              onOpenEvent={handleTimelineSelect}
              onSelectRow={handleRowSelect}
              onVisibleEventsChange={setTimelineVisibleIndices}
            />
            <div className={styles.timeline}>
              {visibleEntries.map(({ entry, index }, visibleIndex) => (
                <DebugTrajectoryTimelineEvent
                  key={`${entry.timestamp}-${entry.type}-${index.toString()}`}
                  entry={entry}
                  eventId={getEventId(index)}
                  expandRequest={
                    expandRequest.index === index ? expandRequest.version : 0
                  }
                  index={index}
                  isLast={visibleIndex === visibleEntries.length - 1}
                  onClearSelection={handleClearSelection}
                  selected={
                    selectedIndices.has(index) ||
                    (selectedIndices.size === 0 && highlightedIndex === index)
                  }
                  onHighlight={setHighlightedIndex}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </ToolLinkContext.Provider>
  );
}
